import { THING_PAGE_NS } from './i18n';
import { FlowTab, IFlow, IStep, IWork, useDetailsPageLeftDrawerProps } from '@diana/core';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ThingNavigation } from './Drawers/Left/Tabs/Navigation/ThingNavigation';
import { Contents } from './Drawers/Left/Tabs/Contents/Contents';
import { ThingActivities } from './Drawers/Left/Tabs/Activities/ThingActivities';
import { WorkPageTabsEnum as WorkTabs } from '../WorkPage/WorkPage.constants';

export interface IUseThingPageLeftDrawerProps {
  thing?: IWork;
  isLoading: boolean;
  flow?: IFlow;
  tabs?: WorkTabs[];
  defaultTab?: WorkTabs;
  peoplePage: boolean;
}

const DefaultThingPageTabs = [WorkTabs.Contents];

export const useThingPageLeftDrawerProps = ({
  thing: entity,
  isLoading,
  flow,
  tabs = DefaultThingPageTabs,
  defaultTab = WorkTabs.Contents,
  peoplePage,
}: IUseThingPageLeftDrawerProps) => {
  const { t } = useTranslation(THING_PAGE_NS);
  // TODO: This is temporary normalizer for steps
  const thing = transformEntitySteps(entity);
  const configTabs = flow?.flowFeatures?.leftPanel?.tabs;
  const configDefaultTab = flow?.flowFeatures?.leftPanel?.defaultTab;

  const tabsToRender = useMemo(
    () => (configTabs ? configTabs.map((t) => t.tab) : tabs),
    [configTabs, tabs],
  );

  const tabsLabels = useMemo(
    () =>
      configTabs
        ? configTabs.reduce((m, t) => {
            m[t.tab as unknown as keyof Record<FlowTab, string>] = t.label;
            return m;
          }, {} as Record<FlowTab, string | undefined>)
        : {},
    [configTabs],
  );

  const resolvedDefaultTab = configDefaultTab
    ? configDefaultTab
    : configTabs
    ? tabsToRender[0]
    : defaultTab;

  const getContents = useCallback(
    (tab?: FlowTab) => {
      switch (tab) {
        case FlowTab.Contents:
          return <Contents thing={thing!} />;
        case FlowTab.Navigation:
          return <ThingNavigation flowId={flow?.id} peoplePage={peoplePage} />;
        case FlowTab.Activities:
          return <ThingActivities thing={thing!} />;
        default:
          return null;
      }
    },
    [thing, flow?.id, peoplePage],
  );

  const tabsItems = {
    contents: getContents,
    labels: useMemo(
      () =>
        tabsToRender
          .map((tab) => ({
            //@ts-ignore
            label: tabsLabels[tab as keyof typeof tabsLabels] ?? t(`tabs.${tab}`),
            value: tab,
          }))
          .filter(({ value }) => tabsToRender.some((v) => v === value)),
      [t, tabsToRender, tabsLabels],
    ),
  };

  return useDetailsPageLeftDrawerProps({
    isLoading,
    tabs: tabsItems,
    currentTab: resolvedDefaultTab,
  });
};

function transformEntitySteps(entity?: IWork) {
  if (!entity) return entity;
  return {
    ...entity,
    steps: entity?.steps?.reduce((prev: any, cur: IStep) => {
      if (cur?.steps.length) {
        prev = [...prev, ...cur.steps];
      }

      return prev;
    }, []),
  };
}
